import { useWorkflowStore } from '@/stores/workflowStore';
import { useLogStore } from '@/stores/logStore';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, Zap, Clock, CheckCircle2, Settings, BookOpen, Library } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function WorkflowDashboard() {
  const { workflows, currentWorkflow } = useWorkflowStore();
  const { logs } = useLogStore();
  const navigate = useNavigate();

  const totalWorkflows = workflows.length;
  const totalNodes = workflows.reduce((sum, wf) => sum + wf.nodes.length, 0);
  const totalConnections = workflows.reduce((sum, wf) => sum + wf.edges.length, 0);
  const successLogs = logs.filter((l) => l.level === 'success').length;
  const errorLogs = logs.filter((l) => l.level === 'error').length;

  const stats = [
    {
      icon: BarChart3,
      label: 'Total Workflows',
      value: totalWorkflows,
      color: 'text-blue-600',
    },
    {
      icon: Zap,
      label: 'Total Nodes',
      value: totalNodes,
      color: 'text-amber-600',
    },
    {
      icon: Clock,
      label: 'Connections',
      value: totalConnections,
      color: 'text-green-600',
    },
    {
      icon: CheckCircle2,
      label: 'Executions',
      value: successLogs + errorLogs,
      color: 'text-purple-600',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <div className="border-b border-border bg-card px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Workflow Dashboard</h1>
            <p className="text-muted-foreground mt-1">Overview of your automation workflows</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button size="lg" onClick={() => navigate('/builder')}>
              Open Builder
            </Button>
            <Button variant="outline" size="lg" onClick={() => navigate('/settings')} className="flex items-center space-x-2">
              <Settings className="w-5 h-5" />
              <span>Settings</span>
            </Button>
            <Button variant="outline" size="lg" onClick={() => navigate('/quick-reference')} className="flex items-center space-x-2">
              <BookOpen className="w-5 h-5" />
              <span>Quick Reference</span>
            </Button>
            <Button variant="outline" size="lg" onClick={() => navigate('/library')} className="flex items-center space-x-2">
              <Library className="w-5 h-5" />
              <span>Library</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <div className="grid md:grid-cols-4 gap-4 mb-12">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className="p-6 space-y-3">
                <div className="flex items-start justify-between">
                  <Icon className={`w-8 h-8 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-3xl font-bold text-foreground mt-1">{stat.value}</p>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Recent Workflows */}
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-4">Your Workflows</h2>
            {workflows.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground mb-4">No workflows yet. Create your first one!</p>
                <Button onClick={() => navigate('/builder')}>Create Workflow</Button>
              </Card>
            ) : (
              <div className="grid gap-4">
                {workflows.map((workflow) => (
                  <Card
                    key={workflow.id}
                    className={`p-6 cursor-pointer transition-all hover:border-primary/50 ${
                      currentWorkflow?.id === workflow.id ? 'border-primary bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-foreground truncate">
                          {workflow.name}
                        </h3>
                        <div className="flex gap-6 mt-3 text-sm">
                          <div className="text-muted-foreground">
                            <span className="font-semibold text-foreground">{workflow.nodes.length}</span> nodes
                          </div>
                          <div className="text-muted-foreground">
                            <span className="font-semibold text-foreground">{workflow.edges.length}</span> connections
                          </div>
                          <div className="text-muted-foreground">
                            Updated {new Date(workflow.updatedAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => navigate('/builder')}>Edit</Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-12 space-y-4">
          <h2 className="text-2xl font-bold text-foreground">Need Help?</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-6 space-y-4">
              <h3 className="font-semibold text-foreground">Getting Started</h3>
              <p className="text-sm text-muted-foreground">
                Learn the basics of creating and managing workflows with our step-by-step guide.
              </p>
              <Button variant="outline" onClick={() => navigate('/help')}>
                View Guide
              </Button>
            </Card>
            <Card className="p-6 space-y-4">
              <h3 className="font-semibold text-foreground">Documentation</h3>
              <p className="text-sm text-muted-foreground">
                Explore detailed documentation about triggers, tools, and actions.
              </p>
              <Button variant="outline" onClick={() => navigate('/help')}>
                Read Docs
              </Button>
            </Card>
          </div>
        </div>

        {/* Quick Navigation Links */}
        <div className="mt-12 border-t border-border pt-6 flex justify-center space-x-6">
          <Button variant="outline" size="lg" onClick={() => navigate('/settings')} className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </Button>
          <Button variant="outline" size="lg" onClick={() => navigate('/library')} className="flex items-center space-x-2">
            <Library className="w-5 h-5" />
            <span>Workflow Library</span>
          </Button>
          <Button variant="outline" size="lg" onClick={() => navigate('/quick-reference')} className="flex items-center space-x-2">
            <BookOpen className="w-5 h-5" />
            <span>Quick Reference Guide</span>
          </Button>
        </div>
      </div>
    </div>
  );
}